import { parseQuestionnaireAccessRequest } from "web_myplann/Api/Nsure/parseQuestionnaireAccessRequest";
import {
	type NsureQuestionnaireAccessRequestDTO,
	ReminderEntityTypeEnum,
	TimelineItemCategoryEnum,
	type TimelineItemResponseDTO,
	TimelineItemSubcategoryEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { EventSourceEnum } from "web_myplann/NotificationCenter/EventAlert/types";
import {
	EVENT_DATE_PARAM,
	EVENT_SOURCE_PARAM,
	EVENT_TYPE_PARAM,
} from "web_myplann/NotificationCenter/Utils/contractEventParams";
import { getZzjBridgeUrl } from "web_myplann/NotificationCenter/Utils/getZzjBridgeUrl";
import { parseClientActionData } from "web_myplann/NotificationCenter/Utils/parseClientActionData";
import { parseContractEventData } from "web_myplann/NotificationCenter/Utils/parseContractEventData";
import { parseReminderData } from "web_myplann/NotificationCenter/Utils/parseReminderData";
import {
	REMINDER_ID_PARAM,
	REMINDER_SCROLL_PARAM,
	REMINDER_SCROLL_VALUE,
} from "web_myplann/NotificationCenter/Utils/reminderScrollParam";

/**
 * Cíl navigace z položky timeline:
 * - `bridge` — navigace delegovaná do nativní RN shell přes `device.navigationRequest` (otevře externí URL mimo WebView)
 * - `route` — interní navigace v rámci webové aplikace přes react-router
 * - `questionnaire` — investiční dotazník: web si vyžádá přístup (nsure) pro už
 *   zvalidovaný request a otevře externí URL přes nativní shell, stejně jako po
 *   prokliku z pushe
 */
export type TimelineNavigation =
	| { kind: "bridge"; url: string }
	| { kind: "route"; path: string }
	| { kind: "questionnaire"; request: NsureQuestionnaireAccessRequestDTO };

const buildReminderEntityRoute = (
	entityType: ReminderEntityTypeEnum,
	entityId: string,
	reminderId?: number | null,
): string => {
	const basePath =
		entityType === ReminderEntityTypeEnum.Portfolio
			? Routing.Portfolio.PortfolioDetail.path(entityId)
			: Routing.Portfolio.PropertyDetail.path(entityId);
	const separator = basePath.includes("?") ? "&" : "?";
	const route = `${basePath}${separator}${REMINDER_SCROLL_PARAM}=${REMINDER_SCROLL_VALUE}`;
	return reminderId != null
		? `${route}&${REMINDER_ID_PARAM}=${reminderId}`
		: route;
};

/**
 * Contract-detail route for a Notification Center event, carrying the deep-link
 * handover params: `eventSource` bypasses server-side dismissal, and
 * `eventType` + `eventDate` resolve that one event even when it falls outside
 * the alert-box visibility window.
 *
 * `entityId` stays the navigation target — the swagger defines it as "the entity
 * the tap opens" and `data.portfolioId` as "shodne s `entityId`". `portfolioId`
 * is deliberately not read: it is redundant here, and this resolver runs in the
 * render path (`TimelineEntry` uses it to decide whether a row is tappable), so
 * it has to stay pure — no telemetry, no side effects.
 */
const buildPortfolioEventRoute = (item: TimelineItemResponseDTO): string => {
	const basePath = Routing.Portfolio.PortfolioDetail.path(item.entityId);
	const eventData = parseContractEventData(item.data);

	const params = new URLSearchParams({
		[EVENT_SOURCE_PARAM]: EventSourceEnum.NotificationCenter,
	});
	if (eventData) {
		params.set(EVENT_TYPE_PARAM, eventData.eventType);
		if (eventData.eventDate) {
			params.set(EVENT_DATE_PARAM, eventData.eventDate);
		}
	}

	const separator = basePath.includes("?") ? "&" : "?";
	return `${basePath}${separator}${params.toString()}`;
};

const REMINDER_SUBCATEGORY_ENTITY_TYPE: Partial<
	Record<TimelineItemSubcategoryEnum, ReminderEntityTypeEnum>
> = {
	[TimelineItemSubcategoryEnum.ReminderPortfolio]:
		ReminderEntityTypeEnum.Portfolio,
	[TimelineItemSubcategoryEnum.ReminderProperty]:
		ReminderEntityTypeEnum.Property,
};

const NOTIFICATION_ROUTE_BUILDERS: Partial<
	Record<TimelineItemSubcategoryEnum, (item: TimelineItemResponseDTO) => string>
> = {
	[TimelineItemSubcategoryEnum.ContractEnd]: buildPortfolioEventRoute,
	[TimelineItemSubcategoryEnum.ContractAnniversary]: buildPortfolioEventRoute,
	[TimelineItemSubcategoryEnum.ContractFixationEnd]: buildPortfolioEventRoute,
	[TimelineItemSubcategoryEnum.NotificationTask]: (item) =>
		Routing.TaskDetail.path(item.entityId),
	[TimelineItemSubcategoryEnum.NotificationActivity]: (item) =>
		Routing.ActivityDetail.path(item.entityId),
};

type RouteBuilder = (item: TimelineItemResponseDTO) => string | null;

const ROUTE_BUILDERS: Record<TimelineItemCategoryEnum, RouteBuilder> = {
	[TimelineItemCategoryEnum.Contract]: buildPortfolioEventRoute,
	[TimelineItemCategoryEnum.Activity]: (item) =>
		Routing.ActivityDetail.path(item.entityId),
	[TimelineItemCategoryEnum.Task]: (item) =>
		Routing.TaskDetail.path(item.entityId),
	[TimelineItemCategoryEnum.Reminder]: (item) => {
		const entityType = item.subcategory
			? REMINDER_SUBCATEGORY_ENTITY_TYPE[item.subcategory]
			: undefined;
		return entityType
			? buildReminderEntityRoute(entityType, item.entityId)
			: null;
	},
	[TimelineItemCategoryEnum.ClientAction]: () => null,
	[TimelineItemCategoryEnum.Notification]: (item) =>
		item.subcategory
			? (NOTIFICATION_ROUTE_BUILDERS[item.subcategory]?.(item) ?? null)
			: null,
};

const resolveTimelineRoute = (item: TimelineItemResponseDTO): string | null => {
	const buildRoute = ROUTE_BUILDERS[item.category];
	return buildRoute ? buildRoute(item) : null;
};

export const resolveTimelineNavigation = (
	item: TimelineItemResponseDTO,
): TimelineNavigation | null => {
	if (
		item.subcategory ===
		TimelineItemSubcategoryEnum.ClientActionInvestmentQuestionnaire
	) {
		// Stejná validace jako u pushe (`getQuestionnaireAccessRequest`), ale bez
		// Sentry/toastu: tohle běží při renderu (`isNavigable`) a `data` s null
		// hodnotami je legitimní stav už vyplněného dotazníku, ne chyba. Vadná
		// položka se prostě nenabídne k prokliku; v sekci „právě řešíte“, kde je
		// klikací vždy, hlásí chybu až proklik (`useNavigateFromResolvingNowItem`).
		const request = parseQuestionnaireAccessRequest(item.data);
		return request ? { kind: "questionnaire", request } : null;
	}

	if (item.category === TimelineItemCategoryEnum.ClientAction) {
		return null;
	}

	if (item.category === TimelineItemCategoryEnum.Notification) {
		if (item.subcategory === TimelineItemSubcategoryEnum.ReminderTerm) {
			const reminderData = parseReminderData(item.data);
			// BE contract for ReminderTerm data isn't guaranteed — skip navigation
			// rather than guessing the target entity.
			if (!reminderData) {
				return null;
			}
			return {
				kind: "route",
				path: buildReminderEntityRoute(
					reminderData.entityType,
					reminderData.entityId ?? item.entityId,
					reminderData.reminderId,
				),
			};
		}

		// The client-action schema is strict (it requires clientActionRequestId
		// alongside url/token), so payloads that aren't client actions fail to
		// parse and fall through to the generic route below.
		const clientActionData = parseClientActionData(item.data);
		if (clientActionData) {
			const bridgeUrl = getZzjBridgeUrl(clientActionData);
			return bridgeUrl ? { kind: "bridge", url: bridgeUrl } : null;
		}
	}

	const path = resolveTimelineRoute(item);
	return path ? { kind: "route", path } : null;
};
